import { useMemo } from 'react';

import { MILLISECONDS_IN_A_DAY } from './constants';

/**
 * Returns the number of days left in the trial
 * @param timeLeft The number of milliseconds until the end of the trial
 */
export function getTrialDaysLeft(timeLeft?: number | null): number | undefined {
	if (timeLeft === null || timeLeft === undefined) {
		return undefined;
	}

	if (timeLeft <= 0) {
		return 0;
	}

	// Use Math.ceil to round up for fractional days
	return Math.ceil(timeLeft / MILLISECONDS_IN_A_DAY);
}

/**
 * Returns the number of days left in the trial
 * @param trialEndTime The timestamp in milliseconds of the end of the trial
 */
export function getTrialDaysLeftFromTrialEndTime(trialEndTime?: number | null): number | undefined {
	if (trialEndTime === null || trialEndTime === undefined) {
		return undefined;
	}

	return getTrialDaysLeft(trialEndTime - Date.now());
}

/**
 * Returns the number of days left in the trial, memoized to reduce re-renders
 * @param trialEndTime The timestamp in milliseconds of the end of the trial
 */
export function useMemoizedTrialDaysLeftFromTrialEndTime(
	trialEndTime?: number | null,
): number | undefined {
	return useMemo(() => getTrialDaysLeftFromTrialEndTime(trialEndTime), [trialEndTime]);
}

/**
 * Returns the number of days left in the trial
 * Mainly for use by Confluence, which uses a date-only string for the trial end date
 * @param trialEndDate The UTC date-only string of the end of the trial
 */
export function getTrialDaysLeftFromTrialEndDate(trialEndDate?: string | null): number | undefined {
	if (trialEndDate === null || trialEndDate === undefined) {
		return undefined;
	}

	// The expected format is YYYY-MM-DD, which is treated as UTC by the JS Date constructor
	return getTrialDaysLeft(new Date(trialEndDate).getTime() - Date.now());
}

/**
 * Returns the number of days left in the trial, memoized to reduce re-renders
 * Mainly for use by Confluence, which uses a date-only string for the trial end date
 * @param trialEndDate The UTC date-only string of the end of the trial
 */
export function useMemoizedTrialDaysLeftFromTrialEndDate(
	trialEndDate?: string | null,
): number | undefined {
	return useMemo(() => getTrialDaysLeftFromTrialEndDate(trialEndDate), [trialEndDate]);
}
